import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import commonUtils from "../../../../../../script/utils/commonUtils";
import wattwaechterRest from "../../../../../../rest/wattwaechterRest";
import fieldArrayResultUtils from "../../../../../../script/utils/fieldArrayResultUtils";
import intl from "react-intl-universal";
import { Legend } from "../../Legend";
import { ResultViewType } from "../ResultViewType";
import { WattwaechterItem } from "./WattwaechterItem";
import { CalcSettingsForm } from "./CalcSettingsForm";
import { Line } from "../../../../../components/Line";
import { Spacer } from "../../../../../components/Spacer";
import { BoxList } from "../../../../../components/BoxList";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { WattwaechterResultDialog } from "./WattwaechterResultDialog";
import { useDebounce } from "../../../../../hooks/useDebounce";
import plotUtils from "../../../../../../script/utils/plotUtils";
import { Button } from "../../../../../components/Button";
import { useSaveEntry } from "../../../useSaveEntry";
import calculationRest from "../../../../../../rest/calculationRest";
import { mdiAdjust } from "@mdi/js";
import { Grid } from "../../../../../components/Grid";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const WattwaechterLayer = props => {
    const { plot, show } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const layerName = "wattwaechterLayer";
    const pointLayerName = "pointLayer";

    const initLoading = useRef(true);

    const { calcSetting } = entry;
    const { imageSetting } = calcSetting;

    const [loadingSlice, setLoadingSlice] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState();
    const [calculations, setCalculations] = useState([]);
    const [resultDialogCalculation, setResultDialogCalculation] = useState();
    const [selectedPoint, setSelectedPoint] = useState(false);
    const [legendData, setLegendData] = useState();
    const [changeableFieldType, setChangeableFieldType] = useState(
        calcSetting.fieldType === "EXHAUSTION_FACTOR_BLMSCHV"
            ? "EXHAUSTION_FACTOR_BLMSCHV"
            : null
    );

    const calculationsRef = useRef(calculations);
    calculationsRef.current = calculations;

    const getCalculationId = object =>
        object?.calculationId
        ?? object?.getObjects?.()
            ?.map(getCalculationId)
            .find(id => id !== undefined);

    const getCalculationIdFromEvent = opt =>
        getCalculationId(opt?.selected?.[0])
        ?? getCalculationId(opt?.target)
        ?? opt?.subTargets
            ?.map(getCalculationId)
            .find(id => id !== undefined);

    const selectCalculationById = calculationId => {
        const index = calculationsRef.current.findIndex(c => c.id === calculationId);
        setSelectedIndex(index >= 0 ? index : undefined);
        return index >= 0 ? calculationsRef.current[index] : undefined;
    };

    const selectedCalculation =
        selectedIndex !== undefined
            ? calculations[selectedIndex]
            : undefined;

    const listCalculations = calculations.filter(c => c.calcArea.name !== "OVERVIEW");
    const selectedListIndex = listCalculations.findIndex(c => c.id === selectedCalculation?.id);

    const {saving, saveEntry} = useSaveEntry();

    const isCalcInProgress = entry?.calcSetting?.calcAreas?.some(calcArea =>
        calcArea.calculations?.some(calculation => !calculation.resultAvailable)
    ) || false;

    const onResultClicked = (opt, calculationId) => {
        const calculation = selectCalculationById(calculationId);
        if (!calculation) {
            setSelectedPoint(false);
            return;
        }

        const layer = plot.getLayer(pointLayerName);
        layer.removeObjects();
        plot.addCircle(layer, opt.scenePoint.x, opt.scenePoint.y, true);
        const {ratioU, ratioV} = plotUtils.calculteAreaSidesClickRatio(opt);
        setSelectedPoint({area: calculation.calcArea, ratioU, ratioV});
    };

    const resultDetails = useRef({});

    const {drawTopViewResult, onBoundaryClipped} = fieldArrayResultUtils;

    useEffect(() => {
        const pointLayer = plot.addLayer(pointLayerName, 180);
        const layer = plot.addLayer(layerName, 110);
        layer.setHandler("selection:created", opt => selectCalculationById(getCalculationIdFromEvent(opt)));
        layer.setHandler("selection:updated", opt => selectCalculationById(getCalculationIdFromEvent(opt)));
        layer.onDeactivate(deactivatingLayer => {
            if (!["measureLayer", "downloadLayer"].includes(deactivatingLayer.name)) {
                layer.removeObjects();
                pointLayer.removeObjects();
                plot.removeColorBar();
            }
            setLegendData();
        });
        layer.setHandler("mouse:down", opt => {
            const calculationId = getCalculationIdFromEvent(opt);
            const isResultTarget = ["image", "rect", "group"].includes(opt?.target?.type);
            if (!isResultTarget || calculationId === undefined) {
                plot.canvas.discardActiveObject();
                plot.canvas.renderAll();
                setSelectedIndex();
                setSelectedPoint(false);
                pointLayer.removeObjects();
            } else {
                onResultClicked(opt, calculationId);
            }
        });
    }, []);

    useEffect(() => {
        if (show && !isCalcInProgress) {
            plot.getLayer(layerName).activate();
            filterCalculations();
        } else {
            initLoading.current = true;
            setSelectedIndex();
        }
    }, [show, isCalcInProgress]);

    useEffect(() => {
        if (show && calculations.length > 0 && initLoading.current) {
            loadSlice();
            loadColorBar(calculations[0].id);
            initLoading.current = false;
        }
    }, [calculations]);

    useEffect(() => {
        if (calcSetting.fieldType !== changeableFieldType
            && (calcSetting.fieldType !== "EXHAUSTION_FACTOR_BLMSCHV" || changeableFieldType !== "PROTECTION_AREA_BLMSCHV")) {
            setChangeableFieldType(
                calcSetting.fieldType === "EXHAUSTION_FACTOR_BLMSCHV"
                    ? "EXHAUSTION_FACTOR_BLMSCHV"
                    : null
            );
        }
    },[entry]);

    const onChange = imageSetting => {
        loadColorBar(calculations[0].id);
        loadSlice();
        entry.imageSetting = imageSetting;
        entryContext.setEntry(entry);
    };

    const loadColorBar = async calculationId => {
        plot.removeColorBar();
        if (calculationId) {
            const { colorScaleLowerLimit, colorScaleUpperLimit } = imageSetting;
            if (!commonUtils.isBlank(colorScaleLowerLimit) && !commonUtils.isBlank(colorScaleUpperLimit)) {
                const colorBarData = await wattwaechterRest.getColorBar(calculationId, imageSetting, changeableFieldType);
                plot.addColorBar(colorBarData);
            }
        }
    };

    const filterCalculations = () => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();
        const calculations = calcSetting.calcAreas
            .flatMap(calcArea => (calcArea.calculations || []).map(calculation => ({ ...calculation, calcArea })))
            .filter(calculation => calculation.calculationType === "WATTWAECHTER")
            .map(calculation => ({ ...calculation }));
        const calcsInDisplayOrderAndAreaHeightOrder = sortCalculations(calculations);
        setCalculations(calcsInDisplayOrderAndAreaHeightOrder);
    };

    const calcAreasIdsToPointAnalysisResultMap = useMemo(() => {
        const map = new Map();
        calcSetting.calcAreas.forEach(ca =>
            map.set(ca.id, ca.calculations?.find(c => c.wattwaechterPointResult)?.wattwaechterPointResult)
        );
        return map;
    },[entry]);

    const loadSlice = async () => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();

        setLoadingSlice(true);
        const calculationsWithSlice = calculations.filter(c => imageSetting.slice < c.wattwaechterResult.numberW);
        const imageDownloadPromises = calculationsWithSlice.map(c => wattwaechterRest.getSlice(c.id, imageSetting, ResultViewType.TOP, changeableFieldType));
        const results = await Promise.all(imageDownloadPromises);
        const drawPromises = calculationsWithSlice.map((calculation, index) => {
            const pointAnalysisResult = calcAreasIdsToPointAnalysisResultMap.get(calculation.calcArea.id);
            const calculationIndex = calculations.indexOf(calculation);
            return drawTopViewResult(
                plot,
                setLegendData,
                calculationIndex,
                layer,
                calculation,
                calculation.calcArea,
                results[index],
                calculation.wattwaechterResult,
                imageSetting.slice,
                imageSetting.fieldMax,
                pointAnalysisResult,
                calculation.calcArea.wattwaechterCalcPoint,
            );
        });
        resultDetails.current = {};
        calculationsWithSlice.forEach((calculation, index) => {
            resultDetails.current[calculation.id] = results[index].details;
        });
        await Promise.all(drawPromises);
        orderResults(calculationsWithSlice);
        setLoadingSlice(false);
    };


    const sortCalculations = calculations =>
        calculations.sort((c, n) => c.wattwaechterResult.displayOrder - n.wattwaechterResult.displayOrder);

    const orderResults = calculations => {
        const layer = plot.getLayer(layerName);
        const allObjects = layer.getObjects();
        const reversedCalculations = [...calculations];
        reversedCalculations.reverse();
        for (const calculation of reversedCalculations) {
            const resultObjects = allObjects.filter(im => im.calculationId === calculation.id);
            for(const obj of resultObjects) {
                layer.removeObject(obj);
                layer.addObject(obj);
            }
        }
    };

    const updateCalculations = calculations => {
        const calcIds = calculations.map(c => c.id);
        for (const calcArea of entry.calcSetting.calcAreas) {
            for (const calculation of calcArea.calculations) {
                const index = calcIds.indexOf(calculation.id);
                if (index > -1 && calculation.wattwaechterResult) {
                    const localCalculation = calculations[index];
                    calculation.wattwaechterResult.displayOrder = localCalculation.wattwaechterResult.displayOrder;
                    calculation.wattwaechterResult.shouldDisplay = localCalculation.wattwaechterResult.shouldDisplay;
                }
            }
        }
        entryContext.setEntry(entry);
    };

    const setPointAnalysisVisibility = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calcAreasIdsToPointAnalysisResultMap.get(calcArea.id).visible = value;
        const layer = plot.getLayer(layerName);
        layer.getObjects().filter(o => o.calculationId === calculation.id && o.mxwellname === "operator ratio")
            .forEach(obj => obj.set({ visible: value && calculation.wattwaechterResult.shouldDisplay }));
        plot.canvas.renderAll();
    };

    const setResultVisibility = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calculation.wattwaechterResult.shouldDisplay = value;
        updateCalculations(calculations);
        const layer = plot.getLayer(layerName);
        layer.getObjects().filter(o => o.calculationId === calculation.id).forEach(obj => obj.set({ visible: value }));
        const pointAnalysisResult = calcAreasIdsToPointAnalysisResultMap.get(calcArea.id);
        if(pointAnalysisResult) {
            setPointAnalysisVisibility(index, pointAnalysisResult.visible);
        }
        plot.canvas.renderAll();
    };

    const onBoundaryClippedChanged = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calculation.wattwaechterResult.boundaryClipped = value;
        updateCalculations(calculations);
        const layer = plot.getLayer(layerName);
        onBoundaryClipped(value, layer, calculation, calcArea, plot, calculation.wattwaechterResult, resultDetails.current[calculation.id], true);
        plot.canvas.renderAll();
    };

    const onChangeOrder = (fromIndex, toIndex) => {
        const selectedCalculationId = selectedCalculation?.id;
        const updatedCalculations = [...calculations];
        const [movedItem] = updatedCalculations.splice(fromIndex, 1);
        updatedCalculations.splice(toIndex, 0, movedItem);
        updatedCalculations.forEach((c, i) => {
            c.wattwaechterResult.displayOrder = i;
        });
        updateCalculations(updatedCalculations);
        setCalculations(updatedCalculations);
        setSelectedIndex(
            selectedCalculationId !== undefined
                ? updatedCalculations.findIndex(c => c.id === selectedCalculationId)
                : undefined
        );
        orderResults(updatedCalculations);
    };

    const onSelect = index => {
        const calculation = listCalculations[index];
        const calculationIndex = calculations.findIndex(c => c.id === calculation?.id);
        if (calculationIndex < 0) {
            return;
        }

        const layer = plot.getLayer(layerName);
        const result = layer.getObjects()
            .find(o => o.calculationId === calculation.id);
        if (result !== undefined) {
            result.set({ hasControls: false });
            plot.canvas.setActiveObject(result);
            plot.canvas.renderAll();
            setSelectedIndex(calculationIndex);
        }
    };

    const debouncedSetResultDialogCalculation = useDebounce(
        setResultDialogCalculation,
        800
    );

    const openDialogCalculation = c => {
        debouncedSetResultDialogCalculation(c);
    };

    const closeDialogCalculation = () => {
        debouncedSetResultDialogCalculation(undefined);
    };

    const startPointAnalysis = async () => {
        if (!selectedPoint?.area?.id) {
            return;
        }
        const entry = entryContext.getEntry();
        if (commonUtils.hasRadioWithoutOperator(entry)) {
            commonUtils.dispatchEvent("exception", { i18nLabel: "missingOperator" });
            return;
        }
        await saveEntry(entry);
        const {area, ratioU, ratioV} = selectedPoint;
        const newEntry = await calculationRest.wattwaechterPoint(entry.id, area.id, ratioU, ratioV, entry.calcSetting.imageSetting.slice);
        entryContext.setEntry(newEntry);
    };

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    simulateLoading
                    disabled={!selectedPoint?.area?.id || saving || calcSetting.fieldType === "PROTECTION_AREA_BLMSCHV"}
                    svgIcon={mdiAdjust}
                    value={intl.get("entry.plan.worstcase.analysePoint")}
                    onClick={() => startPointAnalysis()} />
            </Grid>
            <Spacer variant="horizontal" />
            {calculations.length > 0 && <CalcSettingsForm
                onChange={onChange}
                disabled={loadingSlice}
                imageSetting={imageSetting}
                sliceNum={calcSetting.sliceNum}
                fieldType={calcSetting.fieldType}
                fMin={calculations?.length
                    ? Math.min(...calculations.map(c => c.wattwaechterResult.fmin))
                    : undefined}
                fMax={calculations?.length
                    ? Math.max(...calculations.map(c => c.wattwaechterResult.fmax))
                    : undefined}
                onChangeFieldType={setChangeableFieldType} />}
            <Legend
                portal
                legendData={
                    legendData && selectedCalculation
                        ? {
                            ...legendData,
                            calculation: selectedCalculation,
                            opt: {
                                ...legendData.opt,
                                target:
                                    plot.canvas.getActiveObject()
                                    ?? legendData.opt.target,
                            },
                        }
                        : legendData
                }
                viewType={ResultViewType.TOP}
                calcSetting={calcSetting}
                plot={plot} />
            <Spacer variant="horizontal" />
            <Line />
            <Spacer variant="horizontal" />
            {show &&
                <BoxList
                    onSelect={onSelect}
                    selectedIndex={selectedListIndex >= 0 ? selectedListIndex : undefined}
                    onChangeOrder={(fromIndex, toIndex) =>
                        onChangeOrder(
                            calculations.findIndex(c => c.id === listCalculations[fromIndex]?.id),
                            calculations.findIndex(c => c.id === listCalculations[toIndex]?.id)
                        )
                    }>
                    {listCalculations.map((c, index) => (
                        <WattwaechterItem
                            key={c.id}
                            index={index}
                            calcArea={c.calcArea}
                            shouldDisplay={c.wattwaechterResult.shouldDisplay}
                            onVisibilityChanged={val => setResultVisibility(calculations.findIndex(calc => calc.id === c.id), val)}
                            onResultDialog={() => openDialogCalculation(c)} 
                            boundaryClipped={c.wattwaechterResult.boundaryClipped}
                            resultDetails={resultDetails.current[c.id]}
                            fieldType={changeableFieldType ?? calcSetting.fieldType}
                            onBoundaryClippedChanged={val => onBoundaryClippedChanged(calculations.findIndex(calc => calc.id === c.id), val)}
                            pointAnalysisResultVisible={calcAreasIdsToPointAnalysisResultMap.get(c.calcArea.id)?.visible}
                            onPointAnalysisResultVisibleChanged={val => setPointAnalysisVisibility(calculations.findIndex(calc => calc.id === c.id), val)}/>
                    ))}
                </BoxList>
            }
            {resultDialogCalculation &&
                <WattwaechterResultDialog
                    entry={entry}
                    calcSetting={calcSetting}
                    calculations={calculations}
                    calculation={resultDialogCalculation}
                    onClose={() => closeDialogCalculation()} />
            }
        </LayerStyle>
    );

};